import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, combineLatest, filter, first, map, merge, mergeMap, Observable, of, take, takeWhile, tap, zip } from 'rxjs';
import { LgvModel } from '../../data-models/longevity.model';
import { environment as env } from '../../../environments/environment';
import { LgvAuthMgrService } from './lgv-auth-mgr.service';
import { LgvTSHelper } from '../utility/ts-helper';
import { LgvConstants } from '../utility/lgv-constants';
import { LgvLocalStorageService } from './lgv-local-storage.service';
import { Router } from '@angular/router';
import { error } from 'protractor';

@Injectable({
    providedIn: 'root'
})
export class LgvCompanyService {

    _activeCompany: LgvModel.CompanyInfo = null;
    activeCompany$ = new BehaviorSubject(this._activeCompany);

    _activeCompanyBranding: LgvModel.CompanyBranding = null;
    activeCompanyBranding$ = new BehaviorSubject(this._activeCompanyBranding);

    _companyList: LgvModel.CompanyInfo[] = null;
    companyList$ = new BehaviorSubject(this._companyList);

    get isRealtorCompany$(): Observable<boolean> { return this.activeCompany$.pipe(first(f => f !== null), map(x => { return LgvConstants.isRealEstateBusinessType(x?.businessTypeId) })) }
    get isMortgageCompany$(): Observable<boolean> { return this.activeCompany$.pipe(first(f => f !== null), map(x => { return LgvConstants.isMortgageLenderBusinessType(x?.businessTypeId) })) }

    get brandingAssets$(): Observable<LgvModel.BrandingAsset[]> {
        return this.activeCompanyBranding$.pipe(
            //tap(t => { console.log('tap(): brandingContents:'); console.log(t); }),
            map(m => !!m ? m.assets : []));
    }

    sessionSuffix = '';
    nfmCompanyCodes : string[] = ['nfm', 'mshl', 'element','bluprint'];

    loanApplications$ = new BehaviorSubject(null);

    get brandingAttributes$(): Observable<LgvModel.BrandingAttribute[]> {
        return this.activeCompanyBranding$.pipe(filter(f => f !== null), map(m => m.attributes));
    }

    get brandingColorAttributes$(): Observable<LgvModel.BrandingAttribute[]> { return this.brandingAttributes$.pipe(filter(f => f !== null), map(arr => { return arr?.filter(x => x.name.toUpperCase().includes('COLOR')) })); }

    //get logoDefault$(): Observable<string> { return this.getBrandingAsset$('CompanyLogo'); }
    get logoPrimaryDark$(): Observable<string> { return this.getBrandingAsset$('Company_PrimaryDark', this.activeCompanyBranding$); }
    get logoPrimaryLight$(): Observable<string> { return this.getBrandingAsset$('Company_PrimaryLight', this.activeCompanyBranding$); }
    get logoSquare$(): Observable<string> { return this.getBrandingAsset$('Company_Square', this.activeCompanyBranding$); }
    get logoFavicon$(): Observable<string> { return this.getBrandingAsset$('Company_Favicon', this.activeCompanyBranding$); }
    get heroImage1$(): Observable<string> { return this.getBrandingAsset$('Company_Hero1', this.activeCompanyBranding$); }

    get isDarkHeaderBkg$(): Observable<boolean> {
        return this.activeCompanyBranding$.pipe(filter(f => f !== null && f.attributes.length > 0), map(
            branding => {
                let headerBgColor = this.getBrandingAttribute(this.headerBkgColorKey, branding.attributes);
                if (!headerBgColor) {
                    return false;
                }
                return LgvTSHelper.isDarkColor(headerBgColor.value);
            }
        ));
    }
    get isDarkFooterBkg$(): Observable<boolean> {
        return this.activeCompanyBranding$.pipe(filter(f => f !== null && f.attributes.length > 0), map(
            branding => {
                let footerBgColor = this.getBrandingAttribute(this.footerBkgColorKey, branding.attributes);
                if (!footerBgColor) {
                    return false;
                }
                return LgvTSHelper.isDarkColor(footerBgColor.value);
            }
        ));
    }

    get headerLogoAsset$(): Observable<string> {
        return this.isDarkHeaderBkg$.pipe(
            mergeMap((isDark) => {
                return this.getBrandingAsset$(isDark ? 'Company_PrimaryLight' : 'Company_PrimaryDark', this.activeCompanyBranding$)
            }
            ));
    }
    get footerLogoAsset$(): Observable<string> {
        return this.getFooterLogoAsset$(this.activeCompanyBranding$);
    }


    primaryColorKey = 'PrimaryColor';
    secondaryColorKey = 'SecondaryColor';
    headerBkgColorKey = 'HeaderBkgColor';
    footerBkgColorKey = 'FooterBkgColor';
    hrefColorKey = 'HrefColor';
    standoutTextColorKey = 'StandoutTextColor';
    faviconKey = 'Company_Favicon';
    coBrandedFooterBkgColorKey = 'coBrandedFooterBkgColor';

    constructor(private http: HttpClient,
        private lgvAuthMgrSvc: LgvAuthMgrService,
        private router: Router,
        private lgvLocalStorage: LgvLocalStorageService) {
        this.sessionSuffix = LgvTSHelper.imgSuffix();
        this.setActiveCompany(this.getCompanyCode());
    }

    public setActiveCompanyBranding(branding: LgvModel.CompanyBranding) {
        if (!!branding.disclosures) {
            branding.disclosures = branding.disclosures.sort((x, y) => x.displayOrder - y.displayOrder);
        }
        this.activeCompanyBranding$.next(branding);
        this.applyBrandToCSS(branding);
    }

    public getCompanyCode(): string {

        let rtnVal: string = null;

        let location = window.location;
        let envDomain = '';

        // Check custom sub-domain
        switch (env.envName.toUpperCase()) {
            case 'PROD': envDomain = '.realfinity.io'; break;
            case 'UAT': envDomain = '.uat.realfinity.io'; break;
            case 'STAGING': envDomain = '.staging.realfinity.io'; break;
            case 'DEV': envDomain = '.dev.realfinity.io'; break;
        }

        // kxk: need to test in dev, staging, etc...
        if (location.hostname.includes(envDomain)) {
            let parts = location.host.split('.');
            if (parts.length > 0) {
                console.log('* have custom sub domain * - ' + parts[0]);
                // Map "homedashboard" to "real"
                //console.log('*** setting from subdomain ***');
                rtnVal = parts[0].toUpperCase() === 'HOMEDASHBOARD' ? 'REAL' : parts[0];
            }
        }

        // Check url params
        if (!rtnVal || rtnVal === 'REAL') {
            let paramsString = location.href.split("?")[1];

            if (paramsString && paramsString.length > 0) {
                paramsString.split('&').forEach(param => {
                    let parts = param.split('=');
                    if (parts.length == 2 && parts[0] == 'c') {
                        //console.log('*** setting from url param ***');
                        rtnVal = parts[1];
                    }
                });
            }
        }

        // Check local stroage
        if ((!rtnVal || rtnVal === 'REAL') && this.lgvLocalStorage.haveItem(LgvLocalStorageService.companyCodeKey)) {
            //console.log('*** setting from local storage ***');
            rtnVal = this.lgvLocalStorage.getItem(LgvLocalStorageService.companyCodeKey);
        }

        //console.log('returning: ' + !!rtnVal ? rtnVal : 'REAL');

        return !!rtnVal ? rtnVal : 'REAL';
    }

    public getCompanyList(forceRefresh = false) {

        if (!forceRefresh && this.companyList$.value) {
            return this.companyList$;
        }

        let bldUrl = `${env.LgvApiBaseUrl}/companies?$top=500`;
        this.http.get<any>(bldUrl).pipe(
            take(1))
            .subscribe(res => {
                this.companyList$.next(res.value);
            },
                (err: Error) => {
                    const msg = 'There was an error loading the list of companies';
                    console.log(msg);
                }
            );
    }

    public setActiveCompany(code: string) {
        if (
            !!this.activeCompany$.value &&
            !!this.activeCompanyBranding$.value &&
            this.activeCompany$.value?.companyCode === code
          ) {
            return;
          }

        if (!!this.activeCompany$.value) {
            console.log(`=============== Changing Company from [${this.activeCompany$.value?.companyCode}] to [${code}] =================`);
        }

        if (!!code) {
            this.getCompanyInfoByCode(code).subscribe(x => {
              this.activeCompany$.next(x);
            });
      
            this.getBrandingByCode(code).subscribe(x => {
              this.setActiveCompanyBranding(x);
            });
      
            if (code.toUpperCase() !== 'REAL') {
              this.lgvLocalStorage.setItem(LgvLocalStorageService.companyCodeKey, code);
            }
          }
    }

    public getCachedCompanyByCode(code: string): Observable<LgvModel.CompanyInfo> {
        return this.companyList$.pipe(filter(f => f !== null), map(array => array.find(x => x.companyCode === code)));
    }

    public getById(id: string): Observable<LgvModel.CompanyInfo> {
        return this.http.get<LgvModel.CompanyInfo>(`${env.LgvApiBaseUrl}/companies/${id}`).pipe(
            tap({
                error: err => this.checkCompanyErrorCode(err)
            })
        );
    }    

    public getBrandingById(id: string): Observable<LgvModel.CompanyBranding> {
        //return of(this.geRFBranding());

        return this.http.get<LgvModel.CompanyBranding>(`${env.LgvApiBaseUrl}/companies/${id}/branding`).pipe(
            tap({ 
                error: (err: Error) => { this.checkCompanyErrorCode(err); }
            })
        );
    }

    public getPublicCompanyInfoById(id: string): Observable<LgvModel.CompanyInfo> {
        //return of(this.getRFCompanyInfo());

        return this.http.get<LgvModel.CompanyInfo>(`${env.LgvApiBaseUrl}/public/companies/${id}`).pipe(
            tap({ 
                error: (err: Error) => { this.checkCompanyErrorCode(err); }
            })
        );
    }

    public getCompanyInfoByCode(code: string): Observable<LgvModel.CompanyInfo> {
        //return of(this.getRFCompanyInfo());

        return this.http.get<LgvModel.CompanyInfo>(`${env.LgvApiBaseUrl}/public/companies/by-code/${code}`).pipe(
            tap(t => this.loanApplications$.next(t.applicationCompanies.find(ac => ac.type === 'LoanApplications'))),
            catchError((err: Error) => { this.checkCompanyErrorCode(err); return of(null) })
        );
    }

    public getCompanyPricingEnginePublic(companyCode: string): Observable<LgvModel.ApplicationCompany> {
        return this.getCompanyInfoByCode(companyCode).pipe(
            map((company) => {
                return company.applicationCompanies.find(ac => ac.type === 'Pricing');
            })
        )
    }

    public getPOSCompanyPublic(companyCode: string): Observable<LgvModel.ApplicationCompany> {
        return this.getCompanyInfoByCode(companyCode).pipe(
            map((company) => {
                //console.log('Application Companies:');
                //console.log(company);
                //console.log(company.applicationCompanies);
                return company.applicationCompanies.find(ac => ac.type === 'LoanApplications');
            })
        )
    }

    public getBrandingByCode(code: string): Observable<LgvModel.CompanyBranding> {
        //return of(this.geRFBranding());
        
        return this.http.get<LgvModel.CompanyBranding>(`${env.LgvApiBaseUrl}/public/companies/by-code/${code}/branding`);
    }

    get activeCompanyCode$(): Observable<string> { return this.activeCompany$.pipe(filter(f => f !== null), map(m => m.companyCode)); }
    public isActiveCompanyCode$(code: string): Observable<boolean> { return this.activeCompanyCode$.pipe(map(m => m?.toUpperCase() === code.toUpperCase())); }

    public getBrandingAsset$(name: string, branding$: Observable<LgvModel.CompanyBranding>, useRandomSuffix: boolean = false): Observable<string> {
        return branding$.pipe(
            map(branding => branding.assets),
            map(assets => {
                return !!assets ? this.getBrandingAsset(name, assets, useRandomSuffix) : null;
            })
        )
    }

    public getFooterLogoAsset$(branding$: Observable<LgvModel.CompanyBranding>): Observable<string> {
        return this.isDarkFooterBkg$.pipe(
            mergeMap((isDark) => {
                return this.getBrandingAsset$(isDark ? 'Company_PrimaryLight' : 'Company_PrimaryDark', branding$)
            }
            ));
    }

    public getBrandingAsset(name: string, assets: LgvModel.BrandingAsset[], useRandomSuffix: boolean = false): string {
        if (!!assets == false) {
            return null;
        }

        let asset = assets.find(x => x.name === name);
        let suffix = this.lgvAuthMgrSvc.IsClient || !useRandomSuffix ? '' : this.sessionSuffix;
        return !!asset ? asset.url + suffix : null;
    }

    public getBrandingAttribute$(code: string): Observable<LgvModel.BrandingAttribute> {
        return this.brandingAttributes$.pipe(
            map(attributes => {
                return this.getBrandingAttribute(code, attributes);
            })
        )
    }
    public getBrandingAttribute(code: string, attributes: LgvModel.BrandingAttribute[]): LgvModel.BrandingAttribute {
        if (!!attributes == false) {
            return null;
        }
        let attribute = attributes.find(x => x.code === code);

        return attribute;
    }

    public postAssetFile(companyId: string, data: FormData): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/companies/${companyId}/assets`;
        return this.http.post<any>(bldUrl, data);
    }

    public updateImgSuffix() {
        this.sessionSuffix = LgvTSHelper.imgSuffix();
    }

    public deleteAssetFile(companyId: string, assetName: string): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/companies/${companyId}/assets/${assetName}`;
        return this.http.delete<any>(bldUrl);
    }

    public saveBranding(companyId: string, model: LgvModel.CompanyBranding): Observable<any> {
        let bldUrl = `${env.LgvApiBaseUrl}/companies/${companyId}/branding`;
        return this.http.put<any>(bldUrl, model);
    }

    public defaultCompanyBranding(): LgvModel.CompanyBranding {
        return {
            assets: [],
            attributes: [],
            disclosures: null
        }
    }

    public checkCompanyErrorCode(err: Error) {
        if (err instanceof HttpErrorResponse) {
            const httpError: HttpErrorResponse = err as HttpErrorResponse;

            if (httpError?.status === 404) {
                this.router.navigate(['/default/company-not-found']);
            }
        }
    }

    //#region CSS
    setCSSProperty(property: string, color: string) {
        const docStyle = document.documentElement.style;
        docStyle.setProperty(property, color);
    }

    applyBrandToCSS(brand: LgvModel.CompanyBranding) {

        let primaryColor: LgvModel.BrandingAttribute = this.getBrandingAttribute(this.primaryColorKey, brand.attributes);
        let secondaryColor: LgvModel.BrandingAttribute = this.getBrandingAttribute(this.secondaryColorKey, brand.attributes);
        let headerBkgColor: LgvModel.BrandingAttribute = this.getBrandingAttribute(this.headerBkgColorKey, brand.attributes);
        let footerBkgColor: LgvModel.BrandingAttribute = this.getBrandingAttribute(this.footerBkgColorKey, brand.attributes);
        let hrefColor: LgvModel.BrandingAttribute = this.getBrandingAttribute(this.hrefColorKey, brand.attributes);
        let standoutTextColor: LgvModel.BrandingAttribute = this.getBrandingAttribute(this.standoutTextColorKey, brand.attributes);

        if (!!primaryColor && !!primaryColor.value) {
            this.setCSSProperty('--primaryColor', primaryColor.value);
            this.setCSSProperty('--primaryContrast', LgvTSHelper.getContrastingColor(primaryColor.value));
            const primaryColorRgbString = LgvTSHelper.hexToRgbString(primaryColor.value);
            if (primaryColorRgbString) {
                this.setCSSProperty('--primaryColorRGB', primaryColorRgbString);
            }
        }
        if (!!secondaryColor.value) {
            this.setCSSProperty('--secondaryColor', secondaryColor.value);
            this.setCSSProperty('--secondaryContrast', LgvTSHelper.getContrastingColor(secondaryColor.value));
        }
        if (!!headerBkgColor && !!headerBkgColor.value) {
            this.setCSSProperty('--headerBkgColor', headerBkgColor.value);
            this.setCSSProperty('--headerFontColor', LgvTSHelper.getContrastingColor(headerBkgColor.value));
        }
        if (!!footerBkgColor && !!footerBkgColor.value) {
            this.setCSSProperty('--footerBkgColor', footerBkgColor.value);
            this.setCSSProperty('--footerFontColor', LgvTSHelper.getContrastingColor(footerBkgColor.value));
        }
        if (!!hrefColor && !!hrefColor.value) {
            this.setCSSProperty('--hrefColor', hrefColor.value);
        }
        if (!!standoutTextColor && !!standoutTextColor.value) {
            this.setCSSProperty('--standoutTextColor', standoutTextColor.value);
        }
    }

    applyBrandToCobrandedCSS(brand: LgvModel.CompanyBranding) {

        // *** Maya made the decision to have the co-branded footer use the same background as the primary company footer.
        // Commenting this out for now.

        //    let coBrandedFooterBkgColor: LgvModel.BrandingAttribute = this.getBrandingAttribute(this.footerBkgColorKey, brand.attributes);
        //    if (!!coBrandedFooterBkgColor && !!coBrandedFooterBkgColor.value) {
        //        this.setCSSProperty('--coBrandedFooterBkgColor', coBrandedFooterBkgColor.value);
        //        this.setCSSProperty('--coBrandedFooterFontColor', LgvTSHelper.getContrastingColor(coBrandedFooterBkgColor.value));
        //    }
    }

    //#endregion CSS

    private getRFCompanyInfo(): LgvModel.CompanyInfo{
        return {
            "address1": "929 Alton Road, Suite 500",
            "address2": null,
            "city": "Miami",
            "state": "FL",
            "zipCode": "33139",
            "phone": "(786) 595-3942 ",
            "fax": null,
            "website": "rfm.io",
            "licenseNumber": null,
            "companyLogoUrl": "https://realfinity.io/wp-content/uploads/2023/05/Realfinity-Mortgage-Logo-Purple.png",
            "companyEmail": "info@rfmio",
            "disclaimer": null,
            "businessTypeId": "b053507b-328f-ea11-86e9-000d3a328c22",
            "businessTypeName": "Mortgage Lender",
            "lenderPartnerCompanyId": null,
            "lenderPartnerCompanyName": null,
            "userCount": 73,
            "clientCount": 1007,
            "nmlsid": "2445766",
            "companySocialMedia": [
                {
                    "socialMediaTypeId": "cf3284e6-d0cf-ed11-9f73-000d3a5bbf81",
                    "url": "https://www.facebook.com/profile.php?id=61564255230424"
                },
                {
                    "socialMediaTypeId": "d43284e6-d0cf-ed11-9f73-000d3a5bbf81",
                    "url": "https://www.instagram.com/realfinity.io?igsh=dW9rcjVsbGRwenJs"
                },
                {
                    "socialMediaTypeId": "d03284e6-d0cf-ed11-9f73-000d3a5bbf81",
                    "url": "https://twitter.com/Rfm_io"
                },
                {
                    "socialMediaTypeId": "d33284e6-d0cf-ed11-9f73-000d3a5bbf81",
                    "url": "https://youtube.com/@realfinityio?si=kbQQOHIy-Cnyqd85"
                }
            ],
            "applicationCompanies": [
                {
                    "id": "ad0f7c45-83f3-ed11-8e8b-6045bd05860d",
                    "applicationId": "af02eca8-4f4b-ed11-ade6-000d3a32b31e",
                    "type": "Pricing",
                    "name": "OptimalBlue"
                },
                {
                    "id": "ae0f7c45-83f3-ed11-8e8b-6045bd05860d",
                    "applicationId": "56def613-587c-ef11-bdfb-000d3a320d9c",
                    "type": "LoanApplications",
                    "name": "MeridianLink"
                }
            ],
            "id": "738d5051-4beb-ed11-8e8b-6045bd05860d",
            "name": "Real-Finity Mortgage LLC",
            "companyCode": "rfm"
        };
    }

    private geRFBranding(): LgvModel.CompanyBranding {

        return {
         "assets": [
             {
                 "name": "CompanyLogo",
                 "completeName": "CompanyLogo.png",
                 "url": "https://hdprodwestst.blob.core.windows.net/company-images/738d5051-4beb-ed11-8e8b-6045bd05860d/CompanyLogo.png"
             },
             {
                 "name": "Company_Favicon",
                 "completeName": "Company_Favicon",
                 "url": "https://hdprodwestst.blob.core.windows.net/company-images/738d5051-4beb-ed11-8e8b-6045bd05860d/Company_Favicon"
             },
             {
                 "name": "Company_Hero1",
                 "completeName": "Company_Hero1",
                 "url": "https://hdprodwestst.blob.core.windows.net/company-images/738d5051-4beb-ed11-8e8b-6045bd05860d/Company_Hero1"
             },
             {
                 "name": "Company_PrimaryDark",
                 "completeName": "Company_PrimaryDark",
                 "url": "https://hdprodwestst.blob.core.windows.net/company-images/738d5051-4beb-ed11-8e8b-6045bd05860d/Company_PrimaryDark"
             },
             {
                 "name": "Company_PrimaryLight",
                 "completeName": "Company_PrimaryLight",
                 "url": "https://hdprodwestst.blob.core.windows.net/company-images/738d5051-4beb-ed11-8e8b-6045bd05860d/Company_PrimaryLight"
             },
             {
                 "name": "Company_Square",
                 "completeName": "Company_Square",
                 "url": "https://hdprodwestst.blob.core.windows.net/company-images/738d5051-4beb-ed11-8e8b-6045bd05860d/Company_Square"
             },
             {
                 "name": "ContentImage",
                 "completeName": "ContentImage.png",
                 "url": "https://hdprodwestst.blob.core.windows.net/company-images/738d5051-4beb-ed11-8e8b-6045bd05860d/ContentImage.png"
             },
             {
                 "name": "HDHomeImage",
                 "completeName": "HDHomeImage.png",
                 "url": "https://hdprodwestst.blob.core.windows.net/company-images/738d5051-4beb-ed11-8e8b-6045bd05860d/HDHomeImage.png"
             },
             {
                 "name": "HeaderImage",
                 "completeName": "HeaderImage.png",
                 "url": "https://hdprodwestst.blob.core.windows.net/company-images/738d5051-4beb-ed11-8e8b-6045bd05860d/HeaderImage.png"
             },
             {
                 "name": "OngoingHeaderImage",
                 "completeName": "OngoingHeaderImage.png",
                 "url": "https://hdprodwestst.blob.core.windows.net/company-images/738d5051-4beb-ed11-8e8b-6045bd05860d/OngoingHeaderImage.png"
             },
             {
                 "name": "WelcomeHeaderImage",
                 "completeName": "WelcomeHeaderImage.png",
                 "url": "https://hdprodwestst.blob.core.windows.net/company-images/738d5051-4beb-ed11-8e8b-6045bd05860d/WelcomeHeaderImage.png"
             }
         ],
         "disclosures": [
             {
                 "id": "fb926f16-a17a-ef11-bdfd-000d3a58cc72",
                 "typeId": "bc7f7cb1-8ab8-ed11-a8e0-000d3a5bb6ba",
                 "type": "Link",
                 "title": "Support",
                 "value": "https://support.realfinity.io/hc/en-us",
                 "displayOrder": 5,
                 "isForCobrandedClientOnly": null,
                 "contentLocations": [
                     {
                         "id": "d644a807-8f15-ee11-907c-000d3a37c5e2",
                         "name": "Footer"
                     }
                 ]
             },
             {
                 "id": "a149c83f-0a75-ee11-8925-000d3a5a5762",
                 "typeId": "bc7f7cb1-8ab8-ed11-a8e0-000d3a5bb6ba",
                 "type": "Link",
                 "title": "Customer Use Agreement",
                 "value": "https://rfm.io/legal/customer-use",
                 "displayOrder": 3,
                 "isForCobrandedClientOnly": null,
                 "contentLocations": [
                     {
                         "id": "d644a807-8f15-ee11-907c-000d3a37c5e2",
                         "name": "Footer"
                     }
                 ]
             },
             {
                 "id": "a249c83f-0a75-ee11-8925-000d3a5a5762",
                 "typeId": "bc7f7cb1-8ab8-ed11-a8e0-000d3a5bb6ba",
                 "type": "Link",
                 "title": "Legal",
                 "value": "https://rfm.io/legal",
                 "displayOrder": 4,
                 "isForCobrandedClientOnly": null,
                 "contentLocations": [
                     {
                         "id": "d644a807-8f15-ee11-907c-000d3a37c5e2",
                         "name": "Footer"
                     }
                 ]
             },
             {
                 "id": "a349c83f-0a75-ee11-8925-000d3a5a5762",
                 "typeId": "bc7f7cb1-8ab8-ed11-a8e0-000d3a5bb6ba",
                 "type": "Link",
                 "title": "Privacy Policy",
                 "value": "https://rfm.io/privacy-policy/",
                 "displayOrder": 2,
                 "isForCobrandedClientOnly": null,
                 "contentLocations": [
                     {
                         "id": "d644a807-8f15-ee11-907c-000d3a37c5e2",
                         "name": "Footer"
                     }
                 ]
             },
             {
                 "id": "a449c83f-0a75-ee11-8925-000d3a5a5762",
                 "typeId": "bc7f7cb1-8ab8-ed11-a8e0-000d3a5bb6ba",
                 "type": "Link",
                 "title": "Terms of Use",
                 "value": "https://rfm.io/legal/",
                 "displayOrder": 1,
                 "isForCobrandedClientOnly": null,
                 "contentLocations": [
                     {
                         "id": "d644a807-8f15-ee11-907c-000d3a37c5e2",
                         "name": "Footer"
                     }
                 ]
             }
         ],
         "attributes": [
             {
                 "id": "9849c83f-0a75-ee11-8925-000d3a5a5762",
                 "brandingAttributeId": "be14a5c3-4ee4-ed11-9f72-0022480c13de",
                 "name": "Footer Background Color",
                 "value": "#444444",
                 "code": "FooterBkgColor"
             },
             {
                 "id": "9949c83f-0a75-ee11-8925-000d3a5a5762",
                 "brandingAttributeId": "cf30bbbc-0edb-ed11-9f71-000d3a37c2db",
                 "name": "Application Base URL",
                 "value": "https://homedashboard.realfinity.io/",
                 "code": "ApplicationBaseURL"
             },
             {
                 "id": "9a49c83f-0a75-ee11-8925-000d3a5a5762",
                 "brandingAttributeId": "17e7d8d7-40b1-4b2b-a432-a90299a7b4f6",
                 "name": "Stand Out Text Color",
                 "value": "#4a2f89",
                 "code": "StandoutTextColor"
             },
             {
                 "id": "9b49c83f-0a75-ee11-8925-000d3a5a5762",
                 "brandingAttributeId": "5d3293da-b835-4aa6-b6f6-05aa50e65ad4",
                 "name": "Footer Font Color",
                 "value": "#ffffff",
                 "code": "FooterFontColor"
             },
             {
                 "id": "9c49c83f-0a75-ee11-8925-000d3a5a5762",
                 "brandingAttributeId": "ce30bbbc-0edb-ed11-9f71-000d3a37c2db",
                 "name": "Secondary Color",
                 "value": "#828282",
                 "code": "SecondaryColor"
             },
             {
                 "id": "9d49c83f-0a75-ee11-8925-000d3a5a5762",
                 "brandingAttributeId": "b591768f-4ee4-ed11-9f72-0022480c13de",
                 "name": "Header Background Color",
                 "value": "#4a2f89",
                 "code": "HeaderBkgColor"
             },
             {
                 "id": "9e49c83f-0a75-ee11-8925-000d3a5a5762",
                 "brandingAttributeId": "cd30bbbc-0edb-ed11-9f71-000d3a37c2db",
                 "name": "Primary Color",
                 "value": "#4a2f89",
                 "code": "PrimaryColor"
             },
             {
                 "id": "9f49c83f-0a75-ee11-8925-000d3a5a5762",
                 "brandingAttributeId": "bb7204cc-4ee4-ed11-9f72-0022480c13de",
                 "name": "Link Color",
                 "value": "#4a2f89",
                 "code": "HrefColor"
             },
             {
                 "id": "a049c83f-0a75-ee11-8925-000d3a5a5762",
                 "brandingAttributeId": "e798e36e-b0c9-492e-8cdc-58a4defb1a46",
                 "name": "Header Font Color",
                 "value": "#FFF",
                 "code": "HeaderFontColor"
             }
         ]
     };
 
     }
 
    // #region Mock Data



    // private getMockCompany(): LgvModel.CompanyInfo {
    //    let rtnVal: LgvModel.CompanyInfo = { "id": "b42c8568-27d2-ec11-b656-a085fc8e7cca", "name": "CrossCountry Mortgage", "address1": "123 Any St", "address2": null, "city": "sf", "state": "xx", "zipCode": "555555", "phone": "1111111111", "fax": null, "website": "https://crosscountrymortgage.com/", "licenseNumber": "asdfasdf", "companyLogoUrl": null, "companyEmail": null, "disclaimer": null, "companyCode": "CCM", "businessTypeId": "b053507b-328f-ea11-86e9-000d3a328c22", "businessTypeName": "Mortgage Lender", "lenderPartnerCompanyId": null, "lenderPartnerCompanyName": null, "nmlsid": "3029", "companySocialMedia": [{ "socialMediaTypeId": "d53284e6-d0cf-ed11-9f73-000d3a5bbf81", "url": "https://www.zillow.com" }, { "socialMediaTypeId": "cf3284e6-d0cf-ed11-9f73-000d3a5bbf81", "url": "https://www.facebook.com/" }, { "socialMediaTypeId": "d33284e6-d0cf-ed11-9f73-000d3a5bbf81", "url": "https://www.youtube.com?x=ccm" }, { "socialMediaTypeId": "d13284e6-d0cf-ed11-9f73-000d3a5bbf81", "url": "https://www.google.com?x=y" }, { "socialMediaTypeId": "d63284e6-d0cf-ed11-9f73-000d3a5bbf81", "url": "https://www.yelp.com" }, { "socialMediaTypeId": "d23284e6-d0cf-ed11-9f73-000d3a5bbf81", "url": "https://www.linkedin.com" }, { "socialMediaTypeId": "d43284e6-d0cf-ed11-9f73-000d3a5bbf81", "url": "https://www.instagram.com/asdf" }, { "socialMediaTypeId": "d03284e6-d0cf-ed11-9f73-000d3a5bbf81", "url": "https://www.twitter.com?x=y" }] }

    //    //kxk: todo  Not sure if this will be supplied or we hack it like this
    //    rtnVal.baseUrl = window.location.origin +'/';
    //    return rtnVal;
    // }

    // #endregion Mock Data
}
